import { useState } from 'react';
import { card, Eyebrow, ghostButton, primaryButton, Screen, Title } from '../components/ui';
import { exportCode, importCode } from '../store/persist';
import { clampSetLength } from '../store/state';
import { useApp } from '../store/useApp';

export function Settings({ onBack }: { onBack: () => void }) {
  const { state, dispatch } = useApp();
  const { settings } = state;
  const [code, setCode] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [confirmingReset, setConfirmingReset] = useState(false);

  const set = (patch: Partial<typeof settings>) => dispatch({ type: 'updateSettings', patch });

  return (
    <Screen>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          style={{
            width: 44,
            height: 44,
            flex: 'none',
            borderRadius: 10,
            border: '1px solid var(--line-strong)',
            background: 'var(--surface-2)',
            color: 'var(--text-3)',
            font: '600 16px/1 var(--font-display)',
            cursor: 'pointer',
          }}
        >
          ←
        </button>
        <Title>Settings</Title>
      </div>

      <div style={{ ...card, gap: 14 }}>
        <Eyebrow>Set length</Eyebrow>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Stepper
            value={settings.setLength}
            min={2}
            max={8}
            onChange={(n) => set({ setLength: clampSetLength(n) })}
            label="questions per set"
          />
        </div>
        <div style={{ font: '400 12.5px/1.45 var(--font-body)', color: 'var(--text-4)', textWrap: 'pretty' }}>
          Short is the point. Four questions is about three minutes, which is a
          length you can finish on a bad day. You can always do another set.
        </div>
      </div>

      <div style={{ ...card, gap: 4 }}>
        <Eyebrow>Reading and motion</Eyebrow>
        <Toggle
          label="Reduce motion"
          hint="Turns off every transition and animation in the app."
          checked={settings.reduceMotion}
          onChange={(v) => set({ reduceMotion: v })}
        />
        <Toggle
          label="Buzz on an answer"
          hint="A short vibration when you check. Off is fine."
          checked={settings.haptics}
          onChange={(v) => set({ haptics: v })}
        />
        <Toggle
          label="High-legibility font"
          hint="Atkinson Hyperlegible. Letters and numbers are drawn to be harder to confuse."
          checked={settings.font === 'hyperlegible'}
          onChange={(v) => set({ font: v ? 'hyperlegible' : 'default' })}
        />
      </div>

      <div style={{ ...card, gap: 12 }}>
        <Eyebrow>Backup</Eyebrow>
        <div style={{ font: '400 13px/1.45 var(--font-body)', color: 'var(--text-3)', textWrap: 'pretty' }}>
          Everything lives on this phone and nowhere else — no account, nothing
          uploaded. If you are changing devices, copy this code and paste it into
          the new one.
        </div>
        <textarea
          readOnly
          value={exportCode(state)}
          onFocus={(e) => e.currentTarget.select()}
          aria-label="Your backup code"
          style={{
            width: '100%',
            minHeight: 84,
            padding: 12,
            borderRadius: 12,
            background: 'var(--surface-3)',
            border: '1px solid var(--line-strong)',
            color: 'var(--text-3)',
            font: '400 12px/1.5 ui-monospace, monospace',
            resize: 'vertical',
          }}
        />
        <button
          type="button"
          style={{ ...ghostButton, width: '100%', flex: 'none' }}
          onClick={() => {
            void navigator.clipboard
              ?.writeText(exportCode(state))
              .then(() => setMessage('Code copied.'))
              .catch(() => setMessage('Could not copy — select the text and copy it by hand.'));
          }}
        >
          Copy the code
        </button>

        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Paste a code here to restore"
          aria-label="Paste a backup code to restore"
          style={{
            width: '100%',
            minHeight: 48,
            padding: '0 14px',
            borderRadius: 12,
            background: 'var(--surface-3)',
            border: '1px solid var(--line-strong)',
            color: 'var(--text)',
            font: '400 14px/1 var(--font-body)',
            outline: 'none',
          }}
        />
        <button
          type="button"
          disabled={code.trim().length === 0}
          style={{
            ...ghostButton,
            width: '100%',
            flex: 'none',
            opacity: code.trim().length === 0 ? 0.5 : 1,
          }}
          onClick={() => {
            const restored = importCode(code);
            if (!restored) {
              setMessage('That code did not read as a backup. Check it copied in full.');
              return;
            }
            dispatch({ type: 'hydrate', state: restored });
            setCode('');
            setMessage('Restored.');
          }}
        >
          Restore from a code
        </button>

        {message && (
          <div role="status" style={{ font: '500 13px/1.4 var(--font-body)', color: 'var(--lime)' }}>
            {message}
          </div>
        )}
      </div>

      <div style={{ ...card, gap: 12, border: '1px dashed var(--red-line)' }}>
        <Eyebrow tone="var(--red)">Start over</Eyebrow>
        <div style={{ font: '400 13px/1.45 var(--font-body)', color: 'var(--text-3)', textWrap: 'pretty' }}>
          Wipes every streak, badge and count on this device. There is no undo,
          so grab the backup code above first if there is any doubt.
        </div>
        {confirmingReset ? (
          <div style={{ display: 'flex', gap: 9 }}>
            <button
              type="button"
              style={{ ...primaryButton, background: 'var(--red)', color: '#fff' }}
              onClick={() => {
                dispatch({ type: 'resetProgress' });
                setConfirmingReset(false);
                setMessage('Everything is back to zero.');
              }}
            >
              Yes, wipe it
            </button>
            <button type="button" style={ghostButton} onClick={() => setConfirmingReset(false)}>
              Keep it
            </button>
          </div>
        ) : (
          <button
            type="button"
            style={{ ...ghostButton, width: '100%', flex: 'none', color: 'var(--red)' }}
            onClick={() => setConfirmingReset(true)}
          >
            Erase everything
          </button>
        )}
      </div>
    </Screen>
  );
}

function Stepper({
  value,
  min,
  max,
  onChange,
  label,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
  label: string;
}) {
  const button = (delta: number, symbol: string, name: string) => (
    <button
      type="button"
      aria-label={name}
      disabled={delta < 0 ? value <= min : value >= max}
      onClick={() => onChange(value + delta)}
      style={{
        width: 52,
        height: 52,
        flex: 'none',
        borderRadius: 12,
        border: '1px solid var(--line-strong)',
        background: 'var(--surface-2)',
        color: 'var(--text)',
        font: '700 20px/1 var(--font-display)',
        cursor: 'pointer',
        opacity: (delta < 0 ? value <= min : value >= max) ? 0.35 : 1,
      }}
    >
      {symbol}
    </button>
  );

  return (
    <>
      {button(-1, '−', 'Fewer questions')}
      <div style={{ flex: 1, textAlign: 'center' }}>
        <div className="num" style={{ font: '700 30px/1 var(--font-display)' }}>
          {value}
        </div>
        <div style={{ font: '500 11.5px/1.4 var(--font-body)', color: 'var(--text-4)' }}>{label}</div>
      </div>
      {button(1, '+', 'More questions')}
    </>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        width: '100%',
        minHeight: 60,
        padding: '12px 0',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ font: '600 15px/1.2 var(--font-body)', color: 'var(--text)' }}>{label}</div>
        <div style={{ font: '400 12px/1.4 var(--font-body)', color: 'var(--text-4)', textWrap: 'pretty' }}>
          {hint}
        </div>
      </div>
      <div
        aria-hidden
        style={{
          flex: 'none',
          width: 52,
          height: 30,
          borderRadius: 999,
          padding: 3,
          background: checked ? 'var(--lime)' : 'rgba(255,255,255,.12)',
          border: `1px solid ${checked ? 'var(--lime)' : 'var(--line-strong)'}`,
          transition: 'background var(--motion)',
          display: 'flex',
          justifyContent: checked ? 'flex-end' : 'flex-start',
        }}
      >
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: 999,
            background: checked ? '#11150f' : 'var(--text-4)',
            transition: 'background var(--motion)',
          }}
        />
      </div>
    </button>
  );
}
